import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { HorizontalListData, MessageService, parseNumber } from "common";
import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { UserPermissionService } from "../../user/user-permission/user-permission.service";
import { readWrite } from "../../user/user-permission/user-permission.data";
import { routerTransition } from "../../shared/animations/router.animations";
import { BrokerExData } from "../broker.model";
import { BusinessAdvisorPickerComponent } from "../../shared/business-advisor/business-advisor-picker/business-advisor-picker.component";
import { BrokerService } from "../broker.service";
import { switchMap, take } from 'rxjs/operators';
import { of } from 'rxjs';
import { BrokerProfileAdapter } from './broker-profile.data';
import { AdvisorData, AdvisorFacade } from 'common';

@UntilDestroy()
@Component({
    selector: "ifb-broker-profile",
    templateUrl: "./broker-profile.component.html",
    styleUrls: ["./broker-profile.component.scss"],
    providers: [BrokerProfileAdapter],
    animations: routerTransition,
    standalone: false
})
export class BrokerProfileComponent implements OnInit {
  canEditBrokerInfo = false;
  data: BrokerProfileComponentData;
  advisorData: AdvisorData;
  statusListItems: HorizontalListData[];
  commissionListItems: HorizontalListData[];
  brandingListItems: HorizontalListData[];
  otherInfoListItems: HorizontalListData[];
  brokerActivityListItems: HorizontalListData[];

  constructor(
    private readonly dialog: MatDialog,
    private readonly route: ActivatedRoute,
    private readonly brokerService: BrokerService,
    private readonly userPermissionService: UserPermissionService,
    private readonly messageService: MessageService,
    private readonly adapter: BrokerProfileAdapter,
    private readonly router: Router,
    private readonly advisorFacade: AdvisorFacade,
  ) { }

  ngOnInit(): void {
    this.route.data
      .pipe(untilDestroyed(this))
      .subscribe((data) => this.initData(data));

    this.userPermissionService
      .granted([readWrite("brokers-brokers")])
      .pipe(untilDestroyed(this))
      .subscribe((res) => (this.canEditBrokerInfo = res));
  }

  private initData(data: BrokerProfileComponentData): void {
    this.data = data;
    this.statusListItems = this.adapter.adaptStatusListData(data?.broker);
    this.commissionListItems = this.adapter.adaptCommissionListData(data?.broker);
    this.brandingListItems = this.adapter.adaptBrandingListData(data?.broker);
    this.otherInfoListItems = this.adapter.adaptOtherInfoListData(data?.broker);
    this.brokerActivityListItems = this.adapter.adaptBrokerActivityListData(data?.broker);
    this.refreshAdvisorData(data?.broker?.advisorId);
  }

  private refreshAdvisorData(advisorId?: number): void {
    if (!advisorId) {
      this.advisorData = null;
      return;
    }

    this.advisorFacade.getAdvisor$(advisorId)
      .pipe(take(1))
      .subscribe((advisorData: AdvisorData) => this.advisorData = advisorData);
  }

  changeAdvisor(): void {
    BusinessAdvisorPickerComponent.show(this.dialog)
      .pipe(switchMap((data) => {
        if (!data || !data.id) {
          return of();
        }
        return this.brokerService.updateAdvisor(this.data.broker.id, parseNumber(data.id))
          .pipe(switchMap(() => this.brokerService.get(this.data.broker.id)))
      }))
      .subscribe((brokerData: BrokerExData) => {
        if (brokerData) {
          this.data.broker = brokerData;
          this.refreshAdvisorData(brokerData?.advisorId);
          this.messageService.success(`Advisor successfully changed to ${brokerData.advisorName}`);
        }
      })
  }

  changeApiEnabled(isEnabled: boolean): void {
    this.brokerService
      .updateApiEnabled(this.data.broker.id, isEnabled)
      .subscribe(() => {
        this.brokerService
          .get(this.data.broker.id)
          .subscribe((broker) => (this.data.broker = broker));
      });
  }

  onEditStatus(): void {
    this.router.navigate([`broker/${this.data?.broker?.id}/status`]);
  }

  onEditCommission(): void {
    this.router.navigate([`broker/${this.data?.broker?.id}/commission`]);
  }

  onEditOtherInfo(): void {
    this.router.navigate([`broker/${this.data?.broker?.id}/info`]);
  }
}

export interface BrokerProfileComponentData {
  broker?: BrokerExData;
}
